<template>
  <div class="aiv-hscroll">
    <div ref="viewport" class="aiv-hscroll-viewport" @scroll.passive="onScroll">
      <slot />
    </div>
    <div
      class="aiv-hscroll-fade aiv-hscroll-fade--left"
      :class="{ 'is-visible': canScrollLeft }"
      :style="{ left: `${stickyOffset}px` }"
      aria-hidden="true"
    ></div>
    <div
      class="aiv-hscroll-fade aiv-hscroll-fade--right"
      :class="{ 'is-visible': canScrollRight }"
      aria-hidden="true"
    ></div>
    <transition name="aiv-hscroll-hint">
      <div v-if="hintVisible" class="aiv-hscroll-hint" aria-hidden="true">
        <i class="fas fa-chevron-left"></i>
        <span>Swipe</span>
        <i class="fas fa-chevron-right"></i>
      </div>
    </transition>
  </div>
</template>

<script>
const HINT_SEEN_KEY = "aivHScrollHintSeen";

export default {
  name: "AIVHorizontalScroll",
  props: {
    // Width (px) of a sticky first column the left fade must clear
    stickyOffset: { type: Number, default: 0 },
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false,
      hintVisible: false,
    };
  },
  mounted() {
    this.update();
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.$refs.viewport);
    if (this.$refs.viewport.firstElementChild) {
      this.resizeObserver.observe(this.$refs.viewport.firstElementChild);
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    clearTimeout(this.hintTimer);
  },
  methods: {
    update() {
      const el = this.$refs.viewport;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      this.canScrollLeft = el.scrollLeft > 2;
      this.canScrollRight = el.scrollLeft < maxScroll - 2;
      if (maxScroll > 2) this.maybeShowHint();
    },
    onScroll() {
      const el = this.$refs.viewport;
      if (el && el.scrollLeft > 8) this.dismissHint();
      this.update();
    },
    maybeShowHint() {
      if (this.hintShown) return;
      this.hintShown = true;
      let seen = false;
      try {
        seen = sessionStorage.getItem(HINT_SEEN_KEY) === "1";
        sessionStorage.setItem(HINT_SEEN_KEY, "1");
      } catch (e) {
        // sessionStorage unavailable (private mode) — show the hint anyway
      }
      if (seen) return;
      this.hintVisible = true;
      this.hintTimer = setTimeout(this.dismissHint, 4000);
    },
    dismissHint() {
      this.hintVisible = false;
      clearTimeout(this.hintTimer);
    },
  },
};
</script>

<style scoped>
.aiv-hscroll {
  position: relative;
}

.aiv-hscroll-viewport {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  /* Keep horizontal overscroll inside the grid: without this, iOS treats a
     right-swipe at scrollLeft 0 as browser back-navigation */
  overscroll-behavior-x: contain;
  touch-action: manipulation;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.35) rgba(255, 255, 255, 0.08);
}

/* Keep the scrollbar visible (not overlay) while content overflows */
.aiv-hscroll-viewport::-webkit-scrollbar {
  height: 6px;
}

.aiv-hscroll-viewport::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
}

.aiv-hscroll-viewport::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.35);
  border-radius: 3px;
}

/* Edge fades signal clipped content in that direction */
.aiv-hscroll-fade {
  position: absolute;
  top: 0;
  bottom: 6px;
  width: 28px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.25s ease;
  z-index: 7;
}

.aiv-hscroll-fade.is-visible {
  opacity: 1;
}

.aiv-hscroll-fade--left {
  left: 0;
  background: linear-gradient(to right, rgba(15, 25, 32, 0.65), transparent);
}

.aiv-hscroll-fade--right {
  right: 0;
  background: linear-gradient(to left, rgba(15, 25, 32, 0.65), transparent);
}

.aiv-hscroll-hint {
  position: absolute;
  left: 50%;
  top: 64px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(15, 25, 32, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 999px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  z-index: 8;
  pointer-events: none;
  animation: aiv-hscroll-nudge 1.3s ease-in-out infinite;
}

.aiv-hscroll-hint i {
  font-size: 10px;
  opacity: 0.8;
}

@keyframes aiv-hscroll-nudge {
  0%,
  100% {
    transform: translateX(-50%);
  }
  50% {
    transform: translateX(calc(-50% + 8px));
  }
}

.aiv-hscroll-hint-enter-active,
.aiv-hscroll-hint-leave-active {
  transition: opacity 0.3s ease;
}

.aiv-hscroll-hint-enter,
.aiv-hscroll-hint-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .aiv-hscroll-hint {
    animation: none;
  }
}
</style>
